import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import { Bell, Lock, User, Share2, Eye, EyeOff, Copy, Check } from 'lucide-react';
import { generateProfileURL, copyToClipboard } from '../../lib/shareService';

export const SettingsView: React.FC = () => {
  const { profile, user, refreshProfile } = useAuth();
  const [isPublicProfileEnabled, setIsPublicProfileEnabled] = useState(profile?.public_profile_enabled ?? true);
  const [isSaving, setIsSaving] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const handleTogglePublicProfile = async (enabled: boolean) => {
    if (!profile) return;

    setIsSaving(true);
    try {
      await api.patch('/profiles/me', { publicProfileEnabled: enabled });

      setIsPublicProfileEnabled(enabled);
      await refreshProfile();
    } catch (error) {
      console.error('Error updating public profile setting:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyProfileLink = async () => {
    if (!profile?.public_slug) return;
    const url = generateProfileURL(profile.public_slug);
    try {
      await copyToClipboard(url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 3000);
    } catch (error) {
      console.error('Error copying link:', error);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-slate-900">Settings</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <User className="w-5 h-5 text-slate-700" />
              <h2 className="text-xl font-bold text-slate-900">Account Information</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                <input
                  type="text"
                  value={profile?.full_name || ''}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Role</label>
                <div className="px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-600 capitalize">
                  {profile?.role}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Bell className="w-5 h-5 text-slate-700" />
              <h2 className="text-xl font-bold text-slate-900">Notifications</h2>
            </div>

            <div className="space-y-4">
              <label className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-slate-900">Email Notifications</div>
                  <div className="text-sm text-slate-600">Receive updates via email</div>
                </div>
                <input
                  type="checkbox"
                  defaultChecked
                  className="w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-600"
                />
              </label>

              <label className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-slate-900">Job Matches</div>
                  <div className="text-sm text-slate-600">Get notified about relevant jobs</div>
                </div>
                <input
                  type="checkbox"
                  defaultChecked
                  className="w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-600"
                />
              </label>

              <label className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-slate-900">Compliance Alerts</div>
                  <div className="text-sm text-slate-600">Expiring documents and renewals</div>
                </div>
                <input
                  type="checkbox"
                  defaultChecked
                  className="w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-600"
                />
              </label>

              <label className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-slate-900">Messages</div>
                  <div className="text-sm text-slate-600">New message notifications</div>
                </div>
                <input
                  type="checkbox"
                  defaultChecked
                  className="w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-600"
                />
              </label>
            </div>
          </div>

          {profile?.role === 'contractor' && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <Share2 className="w-5 h-5 text-slate-700" />
                <h2 className="text-xl font-bold text-slate-900">Public Profile</h2>
              </div>

              <div className="space-y-4">
                <label className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-slate-900">Enable Public Profile</div>
                    <div className="text-sm text-slate-600">Allow others to view your profile via shareable link</div>
                  </div>
                  <button
                    onClick={() => handleTogglePublicProfile(!isPublicProfileEnabled)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 ${
                      isPublicProfileEnabled ? 'bg-slate-900' : 'bg-slate-200'
                    } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        isPublicProfileEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>

                {isPublicProfileEnabled && profile?.public_slug && (
                  <div className="pt-4 border-t border-slate-200">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Your Public Profile URL</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={generateProfileURL(profile.public_slug)}
                        readOnly
                        className="flex-1 px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-600 text-sm"
                      />
                      <button
                        onClick={handleCopyProfileLink}
                        className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors flex items-center space-x-2"
                      >
                        {copiedLink ? (
                          <>
                            <Check className="w-4 h-4" />
                            <span>Copied</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            <span>Copy</span>
                          </>
                        )}
                      </button>
                    </div>
                    <div className="mt-3 flex items-center space-x-4 text-sm text-slate-600">
                      <div className="flex items-center space-x-1">
                        <Eye className="w-4 h-4" />
                        <span>{profile.profile_views || 0} views</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Lock className="w-5 h-5 text-slate-700" />
              <h2 className="text-xl font-bold text-slate-900">Security</h2>
            </div>

            <div className="space-y-4">
              <button className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors">
                Change Password
              </button>

              <button className="w-full px-4 py-2 border border-slate-200 text-slate-900 rounded-lg hover:bg-slate-50 transition-colors">
                Enable Two-Factor Authentication
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-slate-900 text-white rounded-xl p-6">
            <h3 className="font-bold text-lg mb-2">Need Help?</h3>
            <p className="text-sm text-slate-300 mb-4">
              Contact our support team for assistance
            </p>
            <button className="w-full bg-white text-slate-900 font-medium py-2 rounded-lg hover:bg-slate-100 transition-colors">
              Contact Support
            </button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-900 mb-4">Quick Links</h3>
            <div className="space-y-2">
              <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                Privacy Policy
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                Terms of Service
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                Help Center
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
